"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { defaultLocale, formatPrice, isValidLocale } from "@/config/locale";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";

export interface ProductCardData {
	id: string;
	name: string;
	slug: string;
	brand?: string | null;
	price: number;
	compareAtPrice?: number | null;
	currency: string;
	/** Set with `pricePresentation` when using reference currency UX */
	locale?: string;
	channel?: string;
	pricePresentation?: ResolvedMoneyPresentation;
	compareAtPresentation?: ResolvedMoneyPresentation | null;
	image: string;
	imageAlt?: string;
	hoverImage?: string | null;
	href: string;
	badge?: "Sale" | "New" | null;
	colors?: { name: string; hex: string }[];
	sizes?: string[];
	category?: { id: string; name: string; slug: string } | null;
	createdAt?: string | null;
	hasVariants?: boolean;
	onQuickAdd?: (productId: string) => void;
	shortDescription?: string | null;
	trustBadges?: string[];
	reviewSummary?: { avgRating: number; count: number } | null;
}

interface ProductCardProps {
	product: ProductCardData;
	priority?: boolean;
}

export function ProductCard({ product, priority = false }: ProductCardProps) {
	const tPlp = useTranslations("plp");
	const pres = product.pricePresentation;
	const loc = product.locale;
	const ch = product.channel;
	const enriched = pres != null && loc != null && ch != null;
	const fallbackLocale = loc != null && isValidLocale(loc) ? loc : defaultLocale;

	return (
		<article className="hover:border-primary/30 group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-all duration-300 hover:shadow-md">
			<Link href={product.href} className="flex flex-1 flex-col">
				<div className="relative aspect-square overflow-hidden bg-secondary">
					<Image
						src={product.image}
						alt={product.imageAlt || product.name}
						fill
						sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
						className="object-contain object-center p-3 transition-transform duration-500 ease-out group-hover:scale-[1.04]"
						priority={priority}
					/>
					{product.badge && (
						<span className="bg-foreground/85 absolute left-0 top-3 rounded-r-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-background backdrop-blur-sm">
							{product.badge === "Sale"
								? tPlp("badgeSale")
								: product.badge === "New"
									? tPlp("badgeNew")
									: product.badge}
						</span>
					)}
				</div>

				<div className="flex flex-1 flex-col gap-1.5 p-3.5 pt-3">
					{product.brand && (
						<p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
							{product.brand}
						</p>
					)}
					<h3 className="line-clamp-2 text-[15px] font-semibold leading-snug tracking-tight group-hover:text-primary">
						{product.name}
					</h3>
					{product.shortDescription && (
						<p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
							{product.shortDescription}
						</p>
					)}
					{product.reviewSummary && product.reviewSummary.count > 0 && (
						<div className="flex items-center gap-1">
							<svg
								className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
								viewBox="0 0 20 20"
								aria-hidden="true"
							>
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
							<span className="text-xs font-medium tabular-nums text-foreground">
								{product.reviewSummary.avgRating.toFixed(1)}
							</span>
							<span className="text-xs text-muted-foreground">({product.reviewSummary.count})</span>
						</div>
					)}
					<div className="mt-auto flex items-baseline gap-2 pt-2">
						{enriched ? (
							<span className="text-base font-bold tabular-nums tracking-tight">
								<ReferencePrimaryPrice presentation={pres} locale={loc} channel={ch} size="md" />
							</span>
						) : (
							<span className="text-base font-bold tabular-nums tracking-tight">
								{formatPrice(product.price, product.currency, fallbackLocale)}
							</span>
						)}
						{product.compareAtPrice && enriched && product.compareAtPresentation ? (
							<span className="text-[13px] tabular-nums text-muted-foreground line-through">
								<ReferencePrimaryPrice
									presentation={product.compareAtPresentation}
									locale={loc}
									channel={ch}
									size="sm"
									className="text-[13px] font-normal text-muted-foreground line-through"
								/>
							</span>
						) : product.compareAtPrice ? (
							<span className="text-[13px] tabular-nums text-muted-foreground line-through">
								{formatPrice(product.compareAtPrice, product.currency, fallbackLocale)}
							</span>
						) : null}
					</div>
				</div>
			</Link>
		</article>
	);
}
